import { useState } from 'react';
import { Link } from 'react-router-dom';
import { UserRole, UserStatus } from '@teentack/shared';
import { useProfile } from '@/hooks/use-profile';
import { MyReviews } from '@/features/reviews/MyReviews';
import { PageContainer } from '@/components/PageContainer';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { ArrowRightIcon, BadgeCheckIcon } from '@/components/ui/Icons';
import { ProfileHero } from '@/components/profile/ProfileHero';
import { availabilityLine, locationLabel, workLine } from '@/components/profile/profile-display';
import { CompletenessCard } from '@/components/profile/CompletenessCard';
import { EmptyHint, InfoRow, ProfileSection } from '@/components/profile/ProfileSection';
import { ProfileForm } from '@/components/profile/ProfileForm';

const DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : DATE_FORMAT.format(date);
}

export function ProfilePage() {
  const { data, isPending, isError, error, refetch } = useProfile();
  const [editing, setEditing] = useState(false);

  if (isPending) {
    return (
      <PageContainer>
        <div className="space-y-6" aria-busy="true">
          <Skeleton className="h-52" radius="xl" />
          <Skeleton className="h-40" />
        </div>
      </PageContainer>
    );
  }

  if (isError || !data) {
    return (
      <PageContainer width="narrow">
        <div className="rounded-2xl border border-line bg-white p-8 text-center shadow-soft">
          <h1 className="text-xl font-extrabold text-ink">Could not load your profile</h1>
          <p className="mt-2 text-sm text-slate-500">
            {error instanceof Error ? error.message : 'Something went wrong.'}
          </p>
          <Button className="mt-6" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      </PageContainer>
    );
  }

  const { user, profile } = data;
  const isTeen = profile.role === UserRole.TEEN;
  const isPoster = profile.role === UserRole.JOB_POSTER;
  const availability = availabilityLine(profile);
  const preferences = workLine(profile);

  return (
    <PageContainer>
      <div className="space-y-6">
        <ProfileHero
          user={user}
          profile={profile}
          action={editing ? null : <Button onClick={() => setEditing(true)}>Edit profile</Button>}
        />

        {/* A pending teen cannot apply for work yet — say so before anything else. */}
        {user.status !== UserStatus.ACTIVE && (
          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-sun-200 bg-sun-50 px-5 py-4">
            <BadgeCheckIcon className="size-5 shrink-0 text-sun-700" />
            <p className="flex-1 text-sm font-medium text-sun-700">
              {user.awaitingParentApproval
                ? 'Your guardian still has to approve this account before you can apply for work.'
                : 'This account is not active yet. Finish verification to unlock applications.'}
            </p>
            <Link
              to="/pending"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-sun-700 hover:underline"
            >
              Details
              <ArrowRightIcon className="size-4" />
            </Link>
          </div>
        )}

        {editing ? (
          <ProfileForm profile={profile} onDone={() => setEditing(false)} />
        ) : (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-start">
            {/* Main column */}
            <div className="space-y-6">
              {(isTeen || isPoster) && (
                <ProfileSection title={isTeen ? 'About me' : 'About my work'}>
                  {profile.bio ? (
                    <p className="text-sm leading-relaxed whitespace-pre-line text-slate-600">
                      {profile.bio}
                    </p>
                  ) : (
                    <EmptyHint>
                      No bio yet. A couple of honest lines make a real difference to who gets in
                      touch.
                    </EmptyHint>
                  )}
                </ProfileSection>
              )}

              {isTeen && (
                <ProfileSection
                  title="Skills"
                  description="What job posters search on."
                  action={
                    <span className="text-xs font-semibold text-slate-400">
                      {profile.skills.length} listed
                    </span>
                  }
                >
                  {profile.skills.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {profile.skills.map((skill) => (
                        <Badge key={skill.publicId} tone="brand">
                          {skill.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <EmptyHint>No skills listed yet — add a few so posters can find you.</EmptyHint>
                  )}
                </ProfileSection>
              )}

              {isTeen && (
                <ProfileSection title="Availability">
                  <dl>
                    <InfoRow label="When you are free" value={availability} />
                    <InfoRow label="Work you want" value={preferences} />
                    <InfoRow label="Notes" value={profile.availability?.notes} />
                    <InfoRow
                      label="Résumé"
                      value={
                        profile.resumeUrl ? (
                          <a
                            href={profile.resumeUrl}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="font-semibold text-brand-600 hover:underline"
                          >
                            View résumé
                          </a>
                        ) : null
                      }
                    />
                  </dl>
                </ProfileSection>
              )}

              <ProfileSection title="Location">
                <dl>
                  <InfoRow label="City and state" value={locationLabel(profile)} />
                  <InfoRow label="ZIP code" value={profile.zipCode} />
                  {isPoster && <InfoRow label="Street address" value={profile.streetAddress} />}
                  <InfoRow label="Country" value={profile.country} />
                </dl>
              </ProfileSection>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <CompletenessCard profile={profile} />

              <MyReviews />

              <ProfileSection title="Account">
                <dl>
                  <InfoRow label="Email" value={user.email} />
                  <InfoRow label="Mobile" value={user.phone} />
                  <InfoRow
                    label="Status"
                    value={
                      <Badge tone={user.status === UserStatus.ACTIVE ? 'safe' : 'sun'}>
                        {user.status}
                      </Badge>
                    }
                  />
                  {isTeen && <InfoRow label="Date of birth" value={profile.dateOfBirth} />}
                  {isTeen && (
                    <InfoRow
                      label="Background check"
                      value={profile.backgroundCheckStatus?.replace(/_/g, ' ').toLowerCase()}
                    />
                  )}
                  <InfoRow label="Member since" value={formatDate(profile.createdAt)} />
                </dl>
              </ProfileSection>
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
}
